import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { ErpDelivery, ErpOrder } from '../../models/orderline';
import { ApiImage } from '../../../core/models/product/image'
import { AddCartEvent, CartProduct } from '../../../cart/models/cart-models';

import { compareNumber } from '../../../core/helpers/sorting'

import * as moment from 'moment'

import { ABSOLUTE_QUANT, RELATIVE_QUANT, ThemeConfig } from '../../../core/helpers/theme-config';
import { PriceDetails } from '../../../core/models/product/price-details';
import { Price } from '../../../core/models/product/price';
import { unitPrice, upwardsQuantity } from '../../../core/helpers/prices';
import chaining from '../../../core/helpers/chaining';
import { hasProps } from '../../../core/helpers/object';
import { FutureDelivery } from './../../models/delivery'

const quantityType = ThemeConfig.get<string>('catalog', 'quantity-type')

const FUTURE = 1
const PAST = 0

@Component({
  selector: 'app-orderline',
  templateUrl: './template.html',
  styleUrls: ['./styles.scss'],
  //host: { 'class': 'tr' },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderlineComponent implements OnChanges {
  _productImage: string
  _iterablePast: { month: string, count: number, date: number }[]
  _futureDeliveries: FutureDelivery[]
  startQuantity = 0

  showDeliveriesDetails: boolean = false

  quantity: number

  @Input() order: ErpOrder
  @Input() cartProduct: CartProduct
  @Input() currency: string

  @Output() onAddCart = new EventEmitter<AddCartEvent>()

  get productName(): string {
    return this.order.product.name || "No name"
  }

  get productSubtitle(): string {
    return this.order.product.desc
  }

  get productNumber(): string {
    return this.order.product.code
  }

  get productImage(): string {
    return this._productImage
  }

  get ordered(): number {
    return this.order.totalOrdered
  }

  get hasDelivered(): boolean {
    return this.delivered > 0 && !!this.iterablePast.length
  }

  get delivered(): number {
    return this.order.totalDelivered
  }

  get hasOutstanding(): boolean {
    return this.outstanding > 0 && !!this.futureDeliveries.length
  }

  get outstanding(): number {
    return this.order.totalRemaining
  }

  get futureDeliveries(): FutureDelivery[] {
    return this._futureDeliveries
  }

  get iterablePast(): { month: string, count: number, date: number }[] {
    return this._iterablePast
  }

  get price(): PriceDetails {
    return this.order.product.price
  }

  get currentPrice(): Price {
    return unitPrice(this.order.product, this.actualQuantity || this.minQuantity)
  }

  get minQuantity(): number {
    return quantityType === RELATIVE_QUANT
      ? this.price.minQuantity / this.price.stepping
      : this.price.minQuantity
  }

  get stepping(): number {
    return quantityType === ABSOLUTE_QUANT
      ? this.price.stepping
      : 1
  }

  get actualQuantity(): number {
    return upwardsQuantity(this.order.product, this.quantity || this.startQuantity)
  }

  getActualQuantity(cartQuantity) {
    this.startQuantity = cartQuantity > 0
      ? cartQuantity
      : 0
  }

  get unitPrice(): number {
    return unitPrice(this.order.product, this.actualQuantity).price
  }

  get isCartBusy(): boolean {
    return this.cartProduct && this.cartProduct.busy
  }

  get inCart(): boolean {
    return this.cartProduct && this.cartProduct.inCart
  }

  get cartQuantity(): number {
    return this.cartProduct && this.cartProduct.quantity
  }

  get canBuy(): boolean {
    return hasProps(this.order.product.price)
  }

  // Todo! This should be a setting. For now always true.
  get showItemsInStockForFutureDeliveries(): boolean {
    return true
  }

  ngOnChanges(changes: SimpleChanges) {
    let order = changes.order && changes.order.currentValue

    if(!order)
      return

    if(this.quantity === 1 && quantityType === ABSOLUTE_QUANT && order.product.price)
      this.quantity = order.product.price.minQuantity

    this._productImage = (order.product.assets.imgs
      .find(img => img.type === 'thumb') || {} as ApiImage )
      .src

    this._futureDeliveries = this.createFutureDeliveries(order)
    this._iterablePast = this.createIterablePast(order)

    this.getActualQuantity(this.cartProduct && this.cartProduct.quantity)
  }

  createIterablePast(model: ErpOrder): { month: string, count: number, date: number }[] {
    let past = this.createPastDeliveries(model)

    return Object.keys(past)
      .map(key => ({ month: key, count: past[key], date: Number(moment(key, 'MMMM YYYY')) }))
      .sort(compareNumber(iterable => iterable.date))
  }

  createPastDeliveries(model: ErpOrder): { [month: string]: number } {
    return this.getDeliveries(model, PAST).reduce(
      (dict, delivery) => {
        let month = moment(delivery.date).format('MMMM YYYY')

        return Object.assign(
          dict,
          {
            [month]: (dict[month] || 0) + delivery.delivered
          })
      },
      {}
    )
  }

  createFutureDeliveries(model: ErpOrder): FutureDelivery[] {
    let futureDeliveries = this.getDeliveries(model, FUTURE)
      .map(delivery => ({
        status: "",
        expectedWeek: +moment(delivery.date).format('w'),
        expectedYear: +moment(delivery.date).format('YYYY'),
        count: delivery.remaining,
        orderWeight: Number(moment(delivery.date))
      }))
      .sort(compareNumber(delivery => delivery.orderWeight))

      // Sometimes the expected week is already passed when the order is still open (so still future delivery)
      // This means that the customer hasn't received the items yet (maybe the customer need to pick up the items manually)
      // If this setting is on, aggregate the collection and show them as 'In stock'
      if (this.showItemsInStockForFutureDeliveries) {
        let currentWeek = moment().week()
        let currentYear = moment().year()

        futureDeliveries = futureDeliveries
          .reduce((acc, cur) => {
            if (cur.expectedWeek <= currentWeek && cur.expectedYear <= currentYear) {
              let existing = acc.find(delivery => delivery.status === 'instock')

              if (existing) {
                existing.count += cur.count
              } else {
                cur.status = 'instock'
                acc.push(cur)
              }
            } else {
              cur.status = 'open'

              // Add one week
              cur.expectedWeek = cur.expectedWeek + 1
              acc.push(cur)
            }

            return acc
          }, [])
      }

      return futureDeliveries
  }

  getDeliveries(model: ErpOrder, type: 0 | 1): ErpDelivery[] {
    let now = new Date()

    if (type === PAST) {
      return model.pastdeliveries || []
    } else {
      return model.futuredeliveries || []
    }
  }

  quantityChanged($event: number) {
    this.startQuantity = chaining($event, 'quantity');
    this.onAddCart.emit({
      product: this.order.product._id,
      //product: chaining(this.order.product._id, 'product._id'),
      quantity: chaining($event, 'quantity')
    });
  }

  addCart() {
    this.onAddCart.emit({
      product: this.order.product._id,
      quantity: this.quantity
    })
  }
}
